import { smallTimeOptions } from 'assets/styles/constants';
import { constants } from 'config/constants';
import SuperFetch from 'library/helpers/superFetch';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { InventoryControlActions, Loader, Notification } from 'zComponents/atoms';
import { AssignInventoryAssets, EditDeviceLocationModal, UnassignInventoryAssets } from 'zComponents/molecules';
import { Button, Col, Input, Row, Table } from 'zui/Antd';

const { Search } = Input;

const InventoryControlLocksTable = ({ workspaces }) => {
	const [filteredData, setFilteredData] = useState([]);
	const [filteredInfo, setFilteredInfo] = useState({});
	const [selectedInventoryUIDs, setSelectedInventoryUIDs] = useState([]);
	const [selectedDevice, setSelectedDevice] = useState({});
	const [openAssignModal, setOpenAssignModal] = useState(false);
	const [visibleEditLocation, setVisibleEditLocation] = useState(false);
	const [openUnassignModal, setOpenUnassignModal] = useState(false);

	const [initialData, setInitialData] = useState([]);
	const [loading, setLoading] = useState(true);

	const fetchLocks = () => {
		SuperFetch.get(
			`/controlServer/locks`,
			{},
			{
				page: 1,
				size: 2000
			}
		).then((res) => {
			if (res.statusCode === 200) {
				setInitialData(res.data);
				setFilteredData(res.data);
				setLoading(false);
			} else {
				Notification('error', 'Error while granting permission. Please try again.');
			}
		});
	};

	useEffect(() => {
		fetchLocks();
		return () => {
			setInitialData([]);
		};
	}, []);

	const columns = [
		{
			title: 'ID',
			dataIndex: 'ID',
			key: 'ID',
			width: 70,
			ellipsis: true,
			fixed: 'left',
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['ID'],
						value: lock['ID']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['ID'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['ID'] || null
		},
		{
			title: 'Asset ID',
			dataIndex: ['organisation', 'assetID'],
			key: 'assetID',
			filters: uniqBy(
				initialData
					.filter((lock) => lock.organisation && lock.organisation.assetID !== undefined)
					.map((lock) => {
						return {
							text: lock.organisation.assetID,
							value: lock.organisation.assetID
						};
					}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record.organisation && record.organisation.assetID === value,
			filterSearch: true,
			filteredValue: filteredInfo.assetID || null
		},
		{
			title: 'Lock Name',
			dataIndex: 'alias',
			key: 'alias',
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['alias'],
						value: lock['alias']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['alias'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['alias'] || null
		},
		{
			title: 'Lock Mac',
			dataIndex: 'mac',
			key: 'mac',
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['mac'],
						value: lock['mac']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['mac'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['mac'] || null
		},
		{
			title: 'Auto Lock Time (In Seconds)',
			dataIndex: 'automaticLockSec',
			key: 'automaticLockSec',
			ellipsis: true,
			filters: uniqBy(
				initialData
					.filter((lock) => lock.automaticLockSec !== undefined && lock.automaticLockSec !== null)
					.map((lock) => {
						return {
							text: lock.automaticLockSec,
							value: lock.automaticLockSec
						};
					}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['automaticLockSec'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['automaticLockSec'] || null
		},
		{
			title: 'Remote Control',
			dataIndex: 'remoteControlFlag',
			key: 'remoteControlFlag',
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['remoteControlFlag'],
						value: lock['remoteControlFlag']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['remoteControlFlag'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['remoteControlFlag'] || null
		},
		{
			title: 'Electric Charge',
			dataIndex: 'electricQuantity',
			key: 'electricQuantity',
			sorter: (a, b) => a.electricQuantity - b.electricQuantity,
			ellipsis: true
		},
		{
			title: 'default Name',
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['name'],
						value: lock['name']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['name'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['name'] || null
		},
		{
			title: 'Double Lock Flag',
			dataIndex: 'doubleLockFlag',
			key: 'doubleLockFlag',
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['doubleLockFlag'],
						value: lock['doubleLockFlag']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['doubleLockFlag'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['doubleLockFlag'] || null
		},
		{
			title: 'Sound Flag',
			dataIndex: 'soundFlag',
			key: 'soundFlag',
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: lock['soundFlag'],
						value: lock['soundFlag']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['soundFlag'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['soundFlag'] || null
		},

		{
			title: 'Date Added',
			dataIndex: 'createDate',
			key: 'createDate',

			render: (text) => new Date(text).toLocaleString('en-AU', smallTimeOptions),
			ellipsis: true,
			filters: uniqBy(
				initialData.map((lock) => {
					return {
						text: new Date(lock.createDate).toLocaleString('en-AU', smallTimeOptions),
						value: new Date(lock.createDate).toLocaleString('en-AU', smallTimeOptions)
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) =>
				new Date(record.createDate).toLocaleString('en-AU', smallTimeOptions) === value,
			filterSearch: true,
			filteredValue: filteredInfo.createDate || null
		},
		{
			title: 'Workspace',
			dataIndex: ['organisation', 'name'],
			key: 'orgName',
			filters: uniqBy(
				initialData
					.filter((gateway) => gateway.organisation && gateway.organisation.name)
					.map((gateway) => {
						return {
							text: gateway.organisation.name,
							value: gateway.organisation.name
						};
					}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record.organisation && record.organisation.name === value,
			filterSearch: true,
			filteredValue: filteredInfo.orgName || null
		},
		{
			title: 'Actions',
			dataIndex: '',
			key: 'actions',
			fixed: 'right',
			width: 250,
			render: (text, record) => (
				<InventoryControlActions
					record={record}
					selectedInventoryUIDs={selectedInventoryUIDs}
					setSelectedInventoryUIDs={setSelectedInventoryUIDs}
					setSelectedDevice={setSelectedDevice}
					setVisibleEditLocation={setVisibleEditLocation}
					setOpenAssignModal={setOpenAssignModal}
					setOpenUnassignModal={setOpenUnassignModal}
				/>
			)
		}
	];

	const clearFilters = () => {
		setFilteredInfo({});
		setFilteredData(initialData);
	};

	const handleSearch = (query, array) => {
		const searchResult = array.filter(
			(object) =>
				object['alias'].toLowerCase().includes(query.toLowerCase()) ||
				object['mac'].toLowerCase().includes(query.toLowerCase()) ||
				object['ID'] === Number(query) ||
				object.organisation['assetID'] === Number(query)
		);
		setFilteredData(searchResult);
	};
	const handleChange = (_pagination, filters) => {
		setFilteredInfo(filters);
	};

	return (
		<div style={{ overflow: 'hidden', padding: '0px 10px 10px 10px' }}>
			<Row justify='start'>
				<Col lg={24} md={24} sm={24} xs={24}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
						<Button size={'small'} onClick={clearFilters}>
							Clear Filter(s)
						</Button>
					</div>
					<Search
						style={{ marginBottom: '10px' }}
						placeholder='Search ID, Asset ID, Lock Name Or Mac Address'
						enterButton='Search'
						loading={false}
						onChange={(e) => {
							handleSearch(e.target.value, initialData);
						}}
						onSearch={(query) => {
							handleSearch(query, initialData);
						}}
					/>
				</Col>
			</Row>
			<Row>
				<Col lg={24} md={24} sm={24} xs={24}>
					{loading ? (
						<Loader />
					) : (
						<Table
							rowKey='ID'
							columns={columns}
							pageSize={500}
							dataSource={filteredData}
							onChange={handleChange}
							scroll={{ x: 1000 }}
							virtual
							rowSelection={{
								selectedRowKeys: selectedInventoryUIDs.map((d) => d.inventoryUID),
								onChange: (selectedRowKeys, selectedRows) => {
									const first = selectedRows[0];
									if (!first) return;

									const firstIsAssigned = !!first.organisation?.orgID;

									const compatibleRows = selectedRows.filter(
										(row) => !!row.organisation?.orgID === firstIsAssigned
									);

									const inventoryDevices = compatibleRows.map((row) => ({
										inventoryUID: row.ID,
										deviceID: row.organisation?.assetID || null,
										orgID: row.organisation?.orgID || null,
										name: row.name || row.alias || 'Unnamed Device'
									}));

									setSelectedInventoryUIDs(inventoryDevices);
								},
								getCheckboxProps: (record) => {
									const isAssigned = !!record.organisation?.orgID;
									const locked =
										selectedInventoryUIDs.length > 0 &&
										!!selectedInventoryUIDs[0].orgID !== isAssigned;

									return {
										disabled: !record.ID || locked
									};
								}
							}}
							onRow={(record) => ({
								onClick: () => {
									setSelectedInventoryUIDs((prev) => {
										const exists = prev.some((item) => item.inventoryUID === record.ID);

										// If already exists, remove it
										if (exists) {
											return prev.filter((item) => item.inventoryUID !== record.ID);
										}

										const isAssigned = !!record.organisation?.orgID;

										// If it's the first selected, simply add it

										if (prev.length === 0) {
											return [
												{
													inventoryUID: record.ID,
													deviceID: record.organisation?.assetID || null,
													orgID: record.organisation?.orgID || null,
													name: record.name || record.alias || 'Unnamed Device'
												}
											];
										}

										// If it's not the first, check if it matches the assignment type
										// and add it if it does, or return the previous state if it doesn't
										// This prevents mixing assigned and unassigned devices in the selection
										const firstIsAssigned = !!prev[0].orgID;

										if (firstIsAssigned === isAssigned) {
											return [
												...prev,
												{
													inventoryUID: record.ID,
													deviceID: record.organisation?.assetID || null,
													orgID: record.organisation?.orgID || null,
													name: record.name || record.alias || 'Unnamed Device'
												}
											];
										}

										return prev; // Return previous state if the assignment type doesn't match
									});
								}
							})}
						/>
					)}
				</Col>
			</Row>

			<AssignInventoryAssets
				workspaces={workspaces}
				devices={selectedInventoryUIDs}
				setDevices={setSelectedInventoryUIDs}
				open={openAssignModal}
				setOpen={setOpenAssignModal}
				refetch={fetchLocks}
				categories={[
					{
						categoryID: constants.ZEZAMII_ACCESS,
						name: 'Zezamii Access'
					},
					{
						categoryID: constants.ZEZAMII_LOCKERS,
						name: 'Zezamii Lockers'
					}
				]}
				platform='Control'
				type='Lock'
			>
				<div />
			</AssignInventoryAssets>

			<UnassignInventoryAssets
				devices={selectedInventoryUIDs}
				setDevices={setSelectedInventoryUIDs}
				open={openUnassignModal}
				setOpen={setOpenUnassignModal}
				refetch={fetchLocks}
				platform='Control'
				type='Lock'
			>
				<div />
			</UnassignInventoryAssets>

			<EditDeviceLocationModal
				editVisible={visibleEditLocation}
				setEditVisible={setVisibleEditLocation}
				deviceID={selectedDevice.deviceID}
				orgID={selectedDevice.orgID}
				orgName={selectedDevice.orgName}
			>
				<div />
			</EditDeviceLocationModal>
		</div>
	);
};

export default InventoryControlLocksTable;
